import React, { Suspense } from 'react';
import { Routes, Route, useLocation } from 'react-router-dom';
import SmartSearchRoute from './components/smartsearch/SmartSearchRoute';
import LaunchGate from './components/countdown/LaunchGate';
import Layout from './components/Layout';
import { ActivityRoute, ComparisonRoute } from './components/profilepage/ComparisonRoutes';
import ErrorBoundary from './components/ErrorBoundary';
import ProtectedRoute from './components/auth/ProtectedRoute';
import { NotificationProvider } from './providers/NotificationContext';
import { CircularProgress, Box, Typography, Button } from '@mui/material';

// Loading component
const LoadingFallback = () => (
  <Box 
    display="flex" 
    justifyContent="center" 
    alignItems="center" 
    minHeight="400px"
  >
    <CircularProgress />
  </Box>
);

const NotFoundPage = () => (
  <Box sx={{ textAlign: 'center', mt: 12, mb: 8 }}>
    <Typography variant="h1" sx={{ fontSize: 'var(--type-display)', fontWeight: 'bold', color: 'text.secondary' }}>
      404
    </Typography>
    <Typography variant="h5" gutterBottom>Page Not Found</Typography>
    <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
      The page you're looking for doesn't exist.
    </Typography>
    <Button variant="contained" onClick={() => window.location.href = '/'}>
      Go to Home
    </Button>
  </Box>
);

// Lazy load components for code splitting

// Auth Components
const ProfilePage = React.lazy(() => import('./components/profilepage/ProfilePage'));
const SettingsPage = React.lazy(() => import('./components/profilepage/SettingsPage'));
const LivingPreferencePage = React.lazy(() => import('./components/profilepage/LivingPreferencePage'));
const RenterToolkitPage = React.lazy(() => import('./components/profilepage/RenterToolkitPage'));
const SharedComparisonPage = React.lazy(() => import('./components/profilepage/SharedComparisonPage'));
const ComparisonInteractionTestPage = React.lazy(() => import('./components/profilepage/ComparisonInteractionTestPage'));

// Other Components
const SmartSearchLanding = React.lazy(() => import('./components/smartsearch/SmartSearchLanding'));
const LandingPreview = React.lazy(() => import('./components/landingpage/LandingPreview'));
const HomePreview = React.lazy(() => import('./components/home_preview/HomePreview'));
const NexaPreview = React.lazy(() => import('./components/nexa_preview/NexaPreview'));
const TestLoginPage = React.lazy(() => import('./components/new_login/TestLoginPage'));
const CompleteThirdPartyRegistration = React.lazy(() => import('./components/new_login/CompleteThirdPartyRegistration'));
const SmsConsentPage = React.lazy(() => import('./components/sms_consent/SmsConsentPage'));

const NewBuildingPage = React.lazy(() => import('./components/new_building/BuildingPage'));
const UnitPage = React.lazy(() => import('./components/unit_page/UnitPage'));
const LeasingAgentPage = React.lazy(() => import('./components/leasingagentpage/LeasingAgentPage'));
const AgentShareSchedulePage = React.lazy(() => import('./components/agent_share/AgentShareSchedulePage'));
const NexaMePage = React.lazy(() => import('./components/nexa_me/NexaMePage'));
const NexaMePublicPage = React.lazy(() => import('./components/nexa_me/NexaMePublicPage'));
const SmartCustomerIntakePage = React.lazy(() => import('./components/bside_smart_customer/SmartCustomerIntakePage'));
// About Us page hidden for now — restore when we ship the page again
// const AboutUsPage = React.lazy(() => import('./components/aboutus/AboutUsPage'));
// Spinning globe test pages removed — no longer needed
// const SpinningGlobeTestLabel = React.lazy(() => import('./components/SpinningGlobeTestLabel'));
// const SpinningGlobeTestNoLabel = React.lazy(() => import('./components/SpinningGlobeTestNoLabel'));
// B-side (business platform) POC pages
const BsideLoginPage = React.lazy(() => import('./components/bside_page_test/BsideLoginPage'));
const AfterEmailAgentVerifyPage = React.lazy(() => import('./components/bside_page_test/AfterEmailAgentVerifyPage'));
const AgentDashboardPage = React.lazy(() => import('./components/bside_page_test/AgentDashboardPage'));
const AgentSchedulePage = React.lazy(() => import('./components/bside_page_test/AgentSchedulePage'));
const AgentListingPage = React.lazy(() => import('./components/bside_page_test/AgentListingPage'));
// Offer is excluded from this release.
// const AgentOfferPage = React.lazy(() => import('./components/bside_page_test/AgentOfferPage'));
const SmartCustomerPage = React.lazy(() => import('./components/bside_smart_customer/SmartCustomerPage'));
const AgentSettingsPage = React.lazy(() => import('./components/bside_page_test/AgentSettingsPage'));
const AgentZoomCallbackPage = React.lazy(() => import('./components/bside_page_test/AgentZoomCallbackPage'));
const AgentNameCardPage = React.lazy(() => import('./components/bside_page_test/AgentNameCardPage'));
const AgentRequestConfirmationPage = React.lazy(() => import('./components/bside_page_test/AgentRequestConfirmationPage'));
const ManagementPlatformApp = React.lazy(() => import('./management-platform/ManagementPlatformApp'));
// Legacy business/admin routes were removed (components no longer exist in this repo)

const AppRoutes: React.FC = () => {
  const location = useLocation();

  if (/^\/(agent|leasing-agent)\/[^/]+$/.test(location.pathname) && new URLSearchParams(location.search).get('booking') === '1') {
    return <ErrorBoundary><NotificationProvider><Suspense fallback={<LoadingFallback />}><Routes>
      <Route path="/agent/:pathKeyword" element={<NexaMePublicPage />} />
      <Route path="/leasing-agent/:id" element={<NexaMePublicPage />} />
    </Routes></Suspense></NotificationProvider></ErrorBoundary>;
  }

  if (location.pathname === '/nexa-preview') {
    return (
      <ErrorBoundary>
        <NotificationProvider>
          <Suspense fallback={<LoadingFallback />}>
            <NexaPreview />
          </Suspense>
        </NotificationProvider>
      </ErrorBoundary>
    );
  }

  if (
    location.pathname === '/home-preview'
    && (process.env.NODE_ENV !== 'production' || window.location.hostname === 'stage.allroomie.com')
  ) {
    return (
      <ErrorBoundary>
        <Suspense fallback={<LoadingFallback />}>
          <HomePreview />
        </Suspense>
      </ErrorBoundary>
    );
  }

  if (location.pathname === '/management' || location.pathname.startsWith('/management/')) {
    return (
      <ErrorBoundary>
        <Suspense fallback={<LoadingFallback />}>
          <Routes>
            <Route path="/management/*" element={<ManagementPlatformApp />} />
          </Routes>
        </Suspense>
      </ErrorBoundary>
    );
  }

  return (
    <ErrorBoundary>
      <NotificationProvider>
          <Layout>
            <Suspense fallback={<LoadingFallback />}>
              <Routes>
              {/* Customer Routes */}
              <Route path="/" element={<LandingPreview />} />
              <Route path="/chat-with-mimi" element={<SmartSearchRoute><SmartSearchLanding /></SmartSearchRoute>} />
              <Route path="/search" element={<SmartSearchRoute><SmartSearchLanding /></SmartSearchRoute>} />

              <Route path="/login" element={<TestLoginPage />} />
              <Route path="/sms-consent" element={<SmsConsentPage />} />
              <Route path="/complete-registration" element={<CompleteThirdPartyRegistration />} />
              {/* <Route path="/about" element={<AboutUsPage />} /> */}
              <Route path="/building/:id" element={<NewBuildingPage />} />
              <Route path="/unit/:id" element={<UnitPage />} />
              <Route path="/agent/:pathKeyword" element={<NexaMePublicPage />} />
              <Route path="/About-us/Henry-Zhao" element={<AgentShareSchedulePage profileSlug="hooli-henryzhao" />} />
              <Route path="/About-us/Calvin-Luo" element={<AgentShareSchedulePage profileSlug="Hooli-cluo" conferencePerson="calvin" />} />
              <Route path="/customer-intake/:pathKeyword" element={<SmartCustomerIntakePage />} />
              <Route path="/share/comparison" element={<SharedComparisonPage />} />
              {(process.env.NODE_ENV !== 'production' || window.location.hostname === 'stage.allroomie.com') && (
                <Route path="/comparison-interaction-test" element={<ComparisonInteractionTestPage />} />
              )}

              {/* B-side POC Routes */}
              <Route path="/business/login" element={<BsideLoginPage />} />
              <Route path="/business/verify" element={<AfterEmailAgentVerifyPage />} />
              <Route path="/business/dashboard" element={
                <ProtectedRoute allowedRoles={['BROKER', 'ADMIN']} unauthenticatedRedirectTo="/business/login">
                  <AgentDashboardPage />
                </ProtectedRoute>
              } />
              <Route path="/business/schedule" element={
                <ProtectedRoute allowedRoles={['BROKER', 'ADMIN']} unauthenticatedRedirectTo="/business/login">
                  <AgentSchedulePage />
                </ProtectedRoute>
              } />
              <Route path="/business/listing" element={
                <ProtectedRoute allowedRoles={['BROKER', 'ADMIN']} unauthenticatedRedirectTo="/business/login">
                  <AgentListingPage />
                </ProtectedRoute>
              } />
              {/* Offer is excluded from this release.
              <Route path="/business/offer" element={
                <ProtectedRoute allowedRoles={['BROKER', 'ADMIN']} unauthenticatedRedirectTo="/business/login">
                  <AgentOfferPage />
                </ProtectedRoute>
              } />
              */}
              <Route path="/business/smart-customer/:customerId?" element={
                <ProtectedRoute allowedRoles={['BROKER', 'ADMIN']} unauthenticatedRedirectTo="/business/login">
                  <SmartCustomerPage />
                </ProtectedRoute>
              } />
              <Route path="/business/settings" element={
                <ProtectedRoute allowedRoles={['BROKER', 'ADMIN']} unauthenticatedRedirectTo="/business/login">
                  <AgentSettingsPage />
                </ProtectedRoute>
              } />
              <Route path="/settings/zoom" element={
                <ProtectedRoute allowedRoles={['BROKER', 'ADMIN']} unauthenticatedRedirectTo="/business/login">
                  <AgentZoomCallbackPage />
                </ProtectedRoute>
              } />
              <Route path="/business/nexa-me" element={
                <ProtectedRoute allowedRoles={['BROKER', 'ADMIN']} unauthenticatedRedirectTo="/business/login">
                  <NexaMePage />
                </ProtectedRoute>
              } />
              <Route path="/business/name-card" element={
                <ProtectedRoute allowedRoles={['BROKER', 'ADMIN']} unauthenticatedRedirectTo="/business/login">
                  <AgentNameCardPage />
                </ProtectedRoute>
              } />
              <Route path="/business/request-confirmation" element={
                <ProtectedRoute allowedRoles={['BROKER', 'ADMIN']} unauthenticatedRedirectTo="/business/login">
                  <AgentRequestConfirmationPage />
                </ProtectedRoute>
              } />
              <Route
                path="/leasing-agent"
                element={
                  <ProtectedRoute>
                    <LeasingAgentPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/leasing-agent/:id"
                element={<NexaMePublicPage />}
              />
              
              {/* Authentication Routes */}
              <Route path="/profile-settings" element={
                <ProtectedRoute>
                  <ProfilePage />
                </ProtectedRoute>
              } />
              <Route path="/activity" element={
                <ProtectedRoute allowedRoles={['CUSTOMER']} redirectTo="/business/settings" suppressRoleNotice>
                  <ActivityRoute />
                </ProtectedRoute>
              } />
              <Route path="/settings" element={
                <ProtectedRoute allowedRoles={['CUSTOMER']} redirectTo="/business/settings" suppressRoleNotice>
                  <SettingsPage />
                </ProtectedRoute>
              } />
              <Route path="/comparison" element={
                <ProtectedRoute allowedRoles={['CUSTOMER']} redirectTo="/business/settings" suppressRoleNotice>
                  <ComparisonRoute />
                </ProtectedRoute>
              } />
              <Route path="/living-preference" element={
                <ProtectedRoute allowedRoles={['CUSTOMER']} redirectTo="/business/settings" suppressRoleNotice>
                  <LivingPreferencePage />
                </ProtectedRoute>
              } />
              <Route path="/renter-toolkit" element={
                <ProtectedRoute allowedRoles={['CUSTOMER']} redirectTo="/business/settings" suppressRoleNotice>
                  <RenterToolkitPage />
                </ProtectedRoute>
              } />
              {/* 404 catch-all */}
              <Route path="*" element={<NotFoundPage />} />
              </Routes>
            </Suspense>
          </Layout>
      </NotificationProvider>
    </ErrorBoundary>
  );
};

const App: React.FC = () => <LaunchGate><AppRoutes /></LaunchGate>;

export default App;
